import React, { ReactNode } from 'react'
import { Logo } from '../components/simple/logo'
import { Button } from '../components/simple/button'
import { SpecialPanel } from '../layouts/components/panel'
import { makeRequest } from '../request'

interface ErrorBoundaryProps {
  children: ReactNode
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
  showMore: boolean
}

export default class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null, showMore: false }
  }

  static getDerivedStateFromError(error: Error) {
    // Update state to show fallback UI
    return { hasError: true, error, showMore: false }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log error information if needed
    console.error('Error caught in ErrorBoundary:', error, errorInfo)

    // Report the error to a route
    makeRequest('/frontend_api/reportFrontendError', {
      error: error.toString(),
      stack: error.stack,
      errorInfo,
    }).catch((reportError) => {
      console.error('Error reporting to the server:', reportError)
      // try again to the session free version
      makeRequest('/api/reportFrontendError', {
        error: error.toString(),
        stack: error.stack,
        errorInfo,
      }).catch((reportError) => {
        console.error('Error reporting to the server fallback:', reportError)
      })
    })
  }

  render() {
    if (this.state.hasError) {
      // Render fallback UI
      return (
        <SpecialPanel>
          <Logo />
          {this.state.error?.message.includes(
            "Failed to execute 'removeChild' on 'Node': The node to be removed is not a child of this node."
          ) ? (
            <>
              <b>Oops! Something went wrong due to external modifications to the page.</b>
              <div>
                Please disable any browser extensions that may be modifying the page and try again. We are sorry for any
                inconvenience.
              </div>
            </>
          ) : (
            <>
              <b>Oops! Something went wrong.</b>
              <div>We are sorry for any inconvenience. Please try again soon. This error has been reported.</div>
            </>
          )}
          <Button logo onClick={() => window.location.reload()}>
            Reload
          </Button>

          {this.state.showMore ? (
            <p className="mt-8 text-sm">
              <b>{this.state.error?.message}</b>
              <br />
              {this.state.error?.stack}
            </p>
          ) : (
            <Button
              small
              onClick={() => {
                this.setState({ showMore: true })
              }}
            >
              Show more
            </Button>
          )}
        </SpecialPanel>
      )
    }

    return this.props.children
  }
}
